// Admin/Tabs/AdminUsersTab.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import "../Styles/Admin-Users.css";

export default function AdminUsersTab({ apiBase, apiKey, user }) {
  // Invite form
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");

  // Users list
  const [admins, setAdmins] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // UI state
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const isSuperAdmin = user?.role === "superadmin";

    console.log("AdminUsersTab user:", user);
console.log("Is superadmin:", isSuperAdmin);
  // ---------------------------
  // API helpers
  // ---------------------------
  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      "Api-Key": apiKey,
    }),
    [apiKey]
  );

const loadAdmins = useCallback(async () => {
  setLoadingList(true);
  setErr("");

  try {
    const res = await fetch(`${apiBase}/admin/users/list`, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        data.error || `Failed to load admin users (${res.status})`
      );
    }

    setAdmins(Array.isArray(data.users) ? data.users : []);
    setSelectedIds([]);
  } catch (e) {
    console.error("Failed to load admin users:", e);

    setErr(e.message || "Failed to load admin users");
  } finally {
    setLoadingList(false);
  }
}, [apiBase]);

  async function invite() {
    setErr("");
    setMsg("");
    setLoading(true);

    try {
      const res = await fetch(`${apiBase}/admin/users/invite`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ username, email }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to invite");

      setMsg(data.message || "Invite sent");
      setUsername("");
      setEmail("");

      // refresh list after inviting
      await loadAdmins();
    } catch (e) {
      setErr(e.message || "Failed to invite");
    } finally {
      setLoading(false);
    }
  }

  async function deleteSelected() {
    setErr("");
    setMsg("");

    if (!selectedIds.length) {
      setErr("Select at least one admin to delete.");
      return;
    }

    // extra safety confirm
    const ok = window.confirm(`Delete ${selectedIds.length} admin account(s)? This cannot be undone.`);
    if (!ok) return;

    setLoading(true);
    try {
      // Expect backend: DELETE `${apiBase}/users` with { ids: [] }
      const res = await fetch(`${apiBase}/admin/users/delete`, {
        method: "DELETE",
        headers,
        credentials: "include",
        body: JSON.stringify({ ids: selectedIds }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to delete users");

      setMsg(data.message || "Deleted selected admin account(s).");
      await loadAdmins();
    } catch (e) {
      setErr(e.message || "Failed to delete users");
    } finally {
      setLoading(false);
    }
  }

  // ---------------------------
  // Selection helpers
  // ---------------------------
  function toggleSelected(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function selectAll() {
  const ids = admins
    .filter(
      (a) =>
        a?._id &&
        a._id !== (user?.id || user?._id || user?.sub) &&
        a.role === "admin"
    )
    .map((a) => a._id);

  setSelectedIds(ids);
}

  function clearSelection() {
    setSelectedIds([]);
  }

  // ---------------------------
  // Load list on mount (superadmin only)
  // ---------------------------
  useEffect(() => {
    if (!isSuperAdmin) return;

    loadAdmins();
  }, [isSuperAdmin, loadAdmins]);

return (
  <section className="admin-users-card">
    <div className="admin-users-header">
      <h2>👤 Admin Users</h2>

      {/* Only superadmins can refresh the user list */}
      {isSuperAdmin && (
        <button
          className="btn ghost"
          onClick={loadAdmins}
          disabled={loadingList || loading}
        >
          {loadingList ? "Refreshing…" : "Refresh list"}
        </button>
      )}
    </div>

    <div className="admin-users-layout">
      {/* LEFT: Invite (visible to all admins) */}
      <div className="admin-users-panel">
        <h3 className="panel-title">Invite Admin User</h3>

        <div className="admin-form-grid">
          <label>
            <span>Username</span>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              placeholder="e.g. joe_admin"
            />
          </label>

          <label>
            <span>Email</span>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. name@email.com"
            />
          </label>
        </div>

        <div className="admin-actions">
          <button className="btn primary" onClick={invite} disabled={loading}>
            {loading ? "Sending…" : "Send Invite"}
          </button>
        </div>
      </div>

      {/* RIGHT: Manage/Delete (superadmin only) */}
      {isSuperAdmin && (
        <div className="admin-users-panel admin-users-panel--right">
          <div className="panel-row">
            <h3 className="panel-title">Manage Admin Accounts</h3>

            <div className="mini-actions">
              <button className="btn ghost mini" onClick={selectAll} disabled={!admins.length}>
                Select all
              </button>
              <button className="btn ghost mini" onClick={clearSelection} disabled={!selectedIds.length}>
                Clear
              </button>
            </div>
          </div>

          <div className="admin-users-list">
            {!admins.length && !loadingList ? (
              <p className="muted small">No admin accounts found.</p>
            ) : (
              admins.map((a) => {
                const id = a?._id;
                
                const currentUserId = user?.id || user?._id || user?.sub;
                const isSelf = id === currentUserId;
                const isProtected = isSelf || a.role === "superadmin";
                
                const checked = id && selectedIds.includes(id);

                return (
                  <label key={id} className={`admin-user-row ${isProtected ? "disabled" : ""}`}>
                    <input
                      type="checkbox"
                      checked={!!checked}
                      disabled={!id || isProtected}
                      onChange={() => toggleSelected(id)}
                    />
                    <div className="admin-user-meta">
                      <strong className="admin-user-name">
                        {a.username || "Unknown"}
                        {isSelf ? " (you)" : ""}
                      </strong>
                      <span className="muted small admin-user-email">{a.email || "—"}</span>
                    </div>
                    <span className={`role-pill role-${a.role || "admin"}`}>
                      {a.role || "admin"}
                    </span>
                  </label>
                );
              })
            )}
          </div>

          <div className="admin-actions admin-actions--between">
            <div className="muted small">
              Selected: <strong>{selectedIds.length}</strong>
            </div>

            <button
              className="btn danger"
              onClick={deleteSelected}
              disabled={loading || !selectedIds.length}
            >
              {loading ? "Deleting…" : "Delete Selected"}
            </button>
          </div>
        </div>
      )}
    </div>

    {err && <p className="status-line error">{err}</p>}
    {msg && <p className="status-line success">{msg}</p>}
  </section>
);
}